import { motion } from "framer-motion";
import { Plus, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

/**
 * KPI con identidad cromática + delta badge opcional.
 *
 * - delta: { delta_pct, direction: "up"|"down"|"flat", label, previo? }
 *   Renderiza un pequeño badge ↑/↓ con color verde/rojo/gris bajo el número.
 */
export default function VibrantKpi({
  label,
  value,
  subtitle,
  color = "var(--legatum-accent)",
  Icon,
  delay = 0,
  pulse = false,
  bar,
  delta,
  goodWhen = "up", // "up" → subir es bueno (verde). "down" → bajar es bueno (verde, p.ej. "Por cobrar")
  onClick,
  onAction,
  actionLabel = "Crear",
  testId,
}) {
  const handleAction = (e) => {
    e.stopPropagation();
    if (onAction) onAction();
  };

  // Soporta tanto hex como tokens CSS (#xxxxxx o var(--legatum-accent))
  const isHex = typeof color === "string" && color.startsWith("#");
  const tintBg = isHex ? `${color}10` : "rgba(201,162,74,0.08)";
  const borderColor = isHex ? `${color}66` : "rgba(201,162,74,0.45)";
  const borderHover = isHex ? `${color}AA` : "rgba(201,162,74,0.75)";
  const iconBg = isHex ? `${color}1F` : "rgba(201,162,74,0.15)";
  const iconBorder = isHex ? `${color}55` : "rgba(201,162,74,0.35)";
  const iconColor = isHex ? color : "var(--legatum-accent)";
  const shadow = isHex
    ? `0 12px 26px -14px ${color}55, 0 2px 4px -2px ${color}30`
    : "0 12px 26px -14px rgba(201,162,74,0.35)";
  const shadowHover = isHex
    ? `0 18px 34px -14px ${color}88, 0 3px 8px -2px ${color}55`
    : "0 18px 34px -14px rgba(201,162,74,0.55)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.28 }}
      whileHover={{ y: -3 }}
      onClick={onClick}
      data-testid={testId}
      className={`relative rounded-xl p-4 md:p-5 overflow-hidden transition-all ${onClick ? "cursor-pointer" : "cursor-default"}`}
      style={{
        background: `linear-gradient(135deg, ${tintBg} 0%, var(--legatum-surface) 60%)`,
        border: `1.5px solid ${borderColor}`,
        boxShadow: shadow,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = borderHover;
        e.currentTarget.style.boxShadow = shadowHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = borderColor;
        e.currentTarget.style.boxShadow = shadow;
      }}
    >
      {/* Glow en esquina superior derecha */}
      <div
        className="absolute -top-10 -right-10 h-32 w-32 rounded-full pointer-events-none blur-3xl"
        style={{ background: isHex ? color : "var(--legatum-accent)", opacity: 0.14 }}
      />

      {/* Botón de acción rápida */}
      {onAction && (
        <button
          type="button"
          onClick={handleAction}
          title={actionLabel}
          data-testid={testId ? `${testId}-action` : undefined}
          className="absolute top-2 right-2 z-10 h-7 w-7 rounded-md flex items-center justify-center bg-[var(--legatum-accent)] hover:brightness-110 text-[var(--legatum-accent-fg)] shadow-md hover:scale-105 transition-transform"
          aria-label={actionLabel}
        >
          <Plus className="h-4 w-4" />
        </button>
      )}

      <div className="relative flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--legatum-text-muted)] truncate flex-1">
          {label}
        </div>
        {Icon && (
          <div
            className="relative h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: iconBg,
              border: `1px solid ${iconBorder}`,
              boxShadow: `inset 0 1px 0 ${iconBorder}, 0 4px 10px -6px ${isHex ? `${color}66` : "rgba(201,162,74,0.4)"}`,
            }}
          >
            {pulse && (
              <span
                className="absolute inset-0 rounded-lg animate-ping opacity-50"
                style={{ background: iconBorder }}
              />
            )}
            <Icon className="h-4 w-4 relative" style={{ color: iconColor }} />
          </div>
        )}
      </div>

      <div
        className="relative mt-2 text-xl md:text-2xl lg:text-3xl font-extrabold tabular-nums leading-tight break-words"
        style={{ color: iconColor, wordBreak: "break-word" }}
      >
        {value}
      </div>

      {delta && (
        (() => {
          const dir = delta.direction || "flat";
          // Si goodWhen="down" invertimos: bajar=verde, subir=rojo
          const positive = goodWhen === "down" ? dir === "down" : dir === "up";
          const negative = goodWhen === "down" ? dir === "up" : dir === "down";
          const c = dir === "flat" ? "#64748B" : positive ? "#0F8A5F" : negative ? "#DC2626" : "#64748B";
          const DirIcon = dir === "flat" ? Minus : (dir === "up" ? ArrowUpRight : ArrowDownRight);
          const pct = delta.delta_pct == null ? 0 : delta.delta_pct;
          const pctTxt = pct === 0 ? "0%" : `${pct > 0 ? "+" : ""}${pct.toLocaleString("es-ES", { maximumFractionDigits: 1 })}%`;
          return (
            <div
              className="relative mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums"
              style={{
                background: `${c}1A`,
                border: `1px solid ${c}55`,
                color: c,
              }}
              data-testid={testId ? `${testId}-delta` : undefined}
              title={delta.previo != null ? `Antes: ${delta.previo.toLocaleString("es-ES")} · ${delta.label || ""}` : delta.label || ""}
            >
              <DirIcon className="h-3 w-3" strokeWidth={2.5} />
              <span>{pctTxt}</span>
              {delta.label && (
                <span className="hidden sm:inline ml-0.5 text-[9px] uppercase tracking-widest opacity-80 font-semibold">
                  {delta.label}
                </span>
              )}
            </div>
          );
        })()
      )}

      {subtitle && (
        <div className="relative mt-1 text-[11px] font-medium text-[var(--legatum-text-secondary)] truncate">
          {subtitle}
        </div>
      )}

      {typeof bar === "number" && (
        <div className="relative mt-3 h-1.5 rounded-full bg-[var(--legatum-elevated)] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(0, Math.min(100, bar))}%` }}
            transition={{ delay: delay + 0.15, duration: 0.5 }}
            className="h-full rounded-full"
            style={{ background: iconColor }}
          />
        </div>
      )}
    </motion.div>
  );
}
